import React from 'react'
import { Stack, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { EditableField } from '../PatientDetails/EditableMeasurementField'
import { calculateBMI } from '../../utils/patient-profile.utils'
import { DoctorPatientProfile } from '../../types/doctorPatients.types'
import { useTranslation } from '../../../../../common/hooks'
import { useNotifications } from '../../../../../common/providers'
import { UPDATE_PATIENT_PROFILE } from '../../../../../operations'
import {
  updatePatientProfile,
  updatePatientProfileVariables,
} from '../../../../../models/graphqlTypes'
import { useMutation } from '@apollo/client'
import { BoxWithLabel } from '../../../../../common/components'

interface PatientDetailsForm {
  height: string
  weight: string
}

interface PatientMeasurementsSectionProps {
  patientProfile: DoctorPatientProfile
  refetchPatientProfile: () => void
}

export const PatientMeasurementsSection: React.FC<
  React.PropsWithChildren<PatientMeasurementsSectionProps>
> = ({ patientProfile, refetchPatientProfile }) => {
  const { t } = useTranslation()
  const { setSuccessToast } = useNotifications()

  const formMethods = useForm<PatientDetailsForm>({
    defaultValues: {
      height: patientProfile.height?.toString() ?? '',
      weight: patientProfile.weight?.toString() ?? '',
    },
    mode: 'onChange',
  })

  const { handleSubmit } = formMethods

  const [updatePatientProfileMutation] = useMutation<
    updatePatientProfile,
    updatePatientProfileVariables
  >(UPDATE_PATIENT_PROFILE, {
    onCompleted: () => {
      refetchPatientProfile()
      setSuccessToast('common:saveSuccessful', { useFullKey: true })
    },
  })

  const saveField = handleSubmit(async (values) => {
    await updatePatientProfileMutation({
      variables: {
        patientId: patientProfile.id,
        updatePatientInput: {
          height: !values.height.length ? undefined : +values.height,
          weight: !values.weight.length ? undefined : +values.weight,
        },
      },
    })
  })

  return (
    <Stack>
      <FormProvider {...formMethods}>
        <Typography variant="body2" fontWeight="700">
          {t('patients:measurementsLabel')}
        </Typography>
        <Stack direction="row">
          <EditableField
            label={t('common:height')}
            name="height"
            onSave={saveField}
          />
          <EditableField
            label={t('common:weight')}
            name="weight"
            onSave={saveField}
          />
          <Stack width={80}>
            <BoxWithLabel size="small" label={t('patients:bmi')}>
              <Stack height="40px" justifyContent="center">
                <Typography variant="body2">
                  {patientProfile.height &&
                    patientProfile.weight &&
                    calculateBMI(patientProfile.height, patientProfile.weight)}
                </Typography>
              </Stack>
            </BoxWithLabel>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  )
}
